import React, { useState, useCallback, useEffect } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import QuizOptions from '../shared/QuizOptions'
import QuizOption from '../shared/QuizOption'
import LazyImage from '../shared/LazyImage'
import { getQuizOptionsData } from '../../constants'
import { loadCharacterAttributes } from '../../utils/api'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackStep3 } from '../../utils/analytics'

const Step2 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const [selectedBodyType, setSelectedBodyType] = useState('')
  const [selectedBreastSize, setSelectedBreastSize] = useState('')
  const [selectedBootySize, setSelectedBootySize] = useState('')
  const [attributesMap, setAttributesMap] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  const location = useLocation()
  const selectedStyle = selections?.welcome || 'Realistic'
  const isHalloween = !!location.pathname.includes('/halloween')

  useEffect(() => {
    ;(async () => {
      const map = await loadCharacterAttributes()
      setAttributesMap(map)
      setIsLoading(false)
    })()
  }, [])

  const bodyTypes = getQuizOptionsData('bodyType', selectedStyle)
  const breastSizes = getQuizOptionsData('breastSize', selectedStyle)
  const bootySizes = getQuizOptionsData('buttSize', selectedStyle)

  // Auto-select first options on mount
  useEffect(() => {
    if (bodyTypes && bodyTypes.length > 0 && !selectedBodyType) {
      setSelectedBodyType(bodyTypes[0].value)
    }
  }, [bodyTypes])

  useEffect(() => {
    if (breastSizes && breastSizes.length > 0 && !selectedBreastSize) {
      setSelectedBreastSize(breastSizes[0].value)
    }
  }, [breastSizes])

  useEffect(() => {
    if (bootySizes && bootySizes.length > 0 && !selectedBootySize) {
      setSelectedBootySize(bootySizes[0].value)
    }
  }, [bootySizes])

  const getAttributeId = (type, label) => {
    if (!attributesMap[type] || !label) return null
    return attributesMap[type][label.trim()] || null
  }

  const handleBodyTypeClick = useCallback(
    value => {
      if (isTransitioning || isLoading) return // блокування при анімації/завантаженні
      setSelectedBodyType(value)
      const id = getAttributeId('bodytype', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap, isTransitioning, isLoading],
  )

  const handleBreastSizeClick = useCallback(
    value => {
      if (isTransitioning || isLoading) return
      setSelectedBreastSize(value)
      const id = getAttributeId('breastsize', value)
      console.log('📝 BreastSize answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap, isTransitioning, isLoading],
  )

  const handleBootySizeClick = useCallback(
    value => {
      if (isTransitioning || isLoading) return
      setSelectedBootySize(value)
      const id = getAttributeId('buttsize', value)
      console.log('📝 ButtSize answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap, isTransitioning, isLoading],
  )

  const handleContinue = useCallback(() => {
    if (isTransitioning || isLoading || !canContinue) return

    console.log('Continue clicked with:', {
      bodyType: selectedBodyType,
      breastSize: selectedBreastSize,
      buttSize: selectedBootySize,
    })

    trackStep3(selectedBodyType, selectedBreastSize, selectedBootySize)

    onAnswer('step2', {
      bodyType: selectedBodyType,
      breastSize: selectedBreastSize,
      buttSize: selectedBootySize,
    })
  }, [selectedBodyType, selectedBreastSize, selectedBootySize, onAnswer, isTransitioning, isLoading])

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return
    onPrevious()
  }, [onPrevious, isTransitioning])

  // Allow continue if all selections are made
  const canContinue = selectedBodyType && selectedBreastSize && selectedBootySize
  const { t } = useTranslation()

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <QuizContent className='step2-design'>
        {/* Body Type Block */}
        <div className='step2-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('body_text')}</span>
              </>
            ) : (
              t('choose_body')
            )}
          </QuizTitle>
          <QuizOptions className='body-options'>
            {bodyTypes.map(type => (
              <div key={type.value} className='option-wrapper'>
                <QuizOption
                  value={type.value}
                  name='bodyType'
                  onClick={() => handleBodyTypeClick(type.value)}
                  className={`body-option ${selectedBodyType === type.value ? 'selected' : selectedBodyType ? 'not-selected' : ''} ${
                    bodyTypes.length % 2 === 0 ? 'even' : 'odd'
                  }`}
                >
                  <div className='body-image'>
                    <LazyImage src={type.image} alt={type.alt} />
                  </div>
                  <div className='image-title'>{t(type.text)}</div>
                </QuizOption>
              </div>
            ))}
          </QuizOptions>
        </div>

        {/* Breast Size Block */}
        <div className='step2-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('breast_text')}</span>
              </>
            ) : (
              t('choose_breast')
            )}
          </QuizTitle>
          <QuizOptions className='breast-options'>
            {breastSizes.map(size => (
              <div key={size.value} className='option-wrapper'>
                <QuizOption
                  value={size.value}
                  name='breastSize'
                  onClick={() => handleBreastSizeClick(size.value)}
                  className={`breast-option ${selectedBreastSize === size.value ? 'selected' : selectedBreastSize ? 'not-selected' : ''} ${
                    breastSizes.length % 2 === 0 ? 'even' : 'odd'
                  }`}
                >
                  <div className='breast-image'>
                    <LazyImage src={size.image} alt={size.alt} />
                  </div>
                  <div className='image-title'>{t(size.text)}</div>
                </QuizOption>
              </div>
            ))}
          </QuizOptions>
        </div>

        {/* Booty Size Block */}
        <div className='step2-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('booty_text')}</span>
              </>
            ) : (
              t('choose_booty')
            )}
          </QuizTitle>
          <QuizOptions className='booty-options'>
            {bootySizes.map(size => (
              <div key={size.value} className='option-wrapper'>
                <QuizOption
                  value={size.value}
                  name='bootySize'
                  onClick={() => handleBootySizeClick(size.value)}
                  className={`booty-option ${selectedBootySize === size.value ? 'selected' : selectedBootySize ? 'not-selected' : ''} ${
                    bootySizes.length % 2 === 0 ? 'even' : 'odd'
                  }`}
                >
                  <div className='booty-image'>
                    <LazyImage src={size.image} alt={size.alt} />
                  </div>
                  <div className='image-title'>{t(size.text)}</div>
                </QuizOption>
              </div>
            ))}
          </QuizOptions>
        </div>

        <div className='navigation-wrapper step2-navigation-wrapper'>
          <button className='previous-button' onClick={handlePrevious} disabled={isTransitioning}>
            {t('previous')}
          </button>
          <button className='continue-button' onClick={handleContinue} disabled={!canContinue || isTransitioning || isLoading}>
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step2
